import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, Zap, BarChart3, Gift } from 'lucide-react';
interface ComingSoonViewProps {
  tab: 'rzone' | 'leaderboard' | 'rewards';
}
const TAB_CONFIG = {
  rzone: {
    icon: Zap,
    label: 'R Zone',
    description: 'Your real-time pipeline dashboard is on its way. Track leads, loans in origination, underwriting, and closings — all in one place.',
    color: 'from-cyan-500 to-blue-600',
    glow: 'rgba(0, 200, 255, 0.3)',
    badge: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20'
  },
  leaderboard: {
    icon: BarChart3,
    label: 'Leaderboard',
    description: 'See how you rank against your team, division, and the entire company. Compete, climb, and claim your spot at the top.',
    color: 'from-violet-500 to-purple-600',
    glow: 'rgba(139, 92, 246, 0.3)',
    badge: 'text-violet-400 bg-violet-500/10 border-violet-500/20'
  },
  rewards: {
    icon: Gift,
    label: 'Reward Center',
    description: 'Earn points for every closed loan, hit tier milestones, and redeem them for exclusive rewards, bonuses, and perks.',
    color: 'from-pink-500 to-rose-600',
    glow: 'rgba(236, 72, 153, 0.3)',
    badge: 'text-pink-400 bg-pink-500/10 border-pink-500/20'
  }
};
export function ComingSoonView({
  tab
}: ComingSoonViewProps) {
  const config = TAB_CONFIG[tab];
  const Icon = config.icon;
  return <div className="flex-1 flex items-center justify-center min-h-[calc(100vh-160px)] p-8" data-id="element-83">
      {/* Ambient glow */}
      <div className="absolute w-[600px] h-[600px] rounded-full blur-[120px] opacity-10 pointer-events-none" style={{
      background: config.glow
    }} data-id="element-84" />

      <motion.div initial={{
      opacity: 0,
      y: 24
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="relative z-10 flex flex-col items-center text-center max-w-lg" data-id="element-85">
        {/* Icon */}
        <motion.div initial={{
        scale: 0.7,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: 0.15,
        type: 'spring',
        stiffness: 200,
        damping: 18
      }} className="relative mb-8" data-id="element-86">
          {/* Outer glow ring */}
          <motion.div animate={{
          scale: [1, 1.12, 1],
          opacity: [0.4, 0.7, 0.4]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className={`absolute inset-[-12px] rounded-3xl bg-gradient-to-br ${config.color} blur-xl opacity-40`} data-id="element-87" />
          <div className={`relative p-6 rounded-3xl bg-gradient-to-br ${config.color} shadow-2xl`} data-id="element-88">
            <Icon size={48} className="text-white drop-shadow-lg" data-id="element-89" />
          </div>
        </motion.div>

        {/* Badge */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-bold uppercase tracking-widest mb-5 ${config.badge}`} data-id="element-90">
          <Rocket size={12} data-id="element-91" />
          Coming Soon
        </motion.div>

        {/* Heading */}
        <motion.h1 initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.32
      }} className="text-4xl font-bold text-white mb-4 leading-tight" data-id="element-92">
          {config.label}
        </motion.h1>

        {/* Description */}
        <motion.p initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="text-slate-400 text-base leading-relaxed max-w-sm" data-id="element-93">
          {config.description}
        </motion.p>

        {/* Divider dots */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="flex items-center gap-2 mt-10" data-id="element-94">
          {[0, 1, 2].map(i => <motion.div key={i} animate={{
          scale: [1, 1.4, 1],
          opacity: [0.3, 1, 0.3]
        }} transition={{
          duration: 1.4,
          repeat: Infinity,
          delay: i * 0.2,
          ease: 'easeInOut'
        }} className={`w-2 h-2 rounded-full bg-gradient-to-br ${config.color}`} data-id="element-95" />)}
        </motion.div>
      </motion.div>
    </div>;
}